import {AdcConjugationMethod} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  CONJUGATION_SITES_MALFORMED_ERROR,
  CONJUGATION_SITES_REQUIRE_METHOD_ERROR,
  CONJUGATION_SITES_REQUIRE_PROTEIN_ERROR,
  CONJUGATION_SITES_UNSUPPORTED_METHOD_ERROR,
  ConjugationSite,
} from 'molbio/adcs/conjugation-sites/AdcConjugationSiteConstants';
import {parseConjugationSitesTypedValue} from 'molbio/adcs/conjugation-sites/AdcConjugationSiteSerialization';
import {
  conjugationSiteKey,
  getConjugationSiteInputs,
  invalidConjugationSitesError,
} from 'molbio/adcs/conjugation-sites/AdcConjugationSiteUtils';
import {ConjugationSiteCandidatesResult} from 'molbio/adcs/conjugation-sites/findConjugationSiteCandidates';
import {ColumnDefinition} from 'notebook/structured-tables/columns/ColumnDefinitions';
import {CustomEvalResult} from 'notebook/tables/constants/CustomEvalConstants';
import * as ExcessUtil from 'notebook/tables/excess/Util';
import {CustomResolutionQueryType} from 'notebook/tables/string-resolution/StringResolutionConstants';
import {ResolutionQueryType} from 'notebook/tables/TableStoreTransforms';
import {isTypedValueEmpty} from 'notebook/tables/typed-values/DataTypes';
import {BaseTypedValueType, TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';
import {
  makeTypedValueFromCustomResolvedValue,
  ResolveQueryFn,
} from 'notebook/tables/typed-values/TypedValueResolution';

type ConjugationSiteRowInputs =
  /** The protein or method is empty. */
  | {status: 'missing'; message: string}
  /** The protein or method is filled in but invalid; that cell reports its own error. */
  | {status: 'invalid'}
  | {status: 'valid'; antibodyId: string; conjugationMethod: AdcConjugationMethod};

function getConjugationSiteRowInputs(cellDependencies: Array<TypedValue | null>): ConjugationSiteRowInputs {
  const [proteinValue = null, conjugationMethodValue = null] = cellDependencies;
  if (proteinValue == null || isTypedValueEmpty(proteinValue)) {
    return {status: 'missing', message: CONJUGATION_SITES_REQUIRE_PROTEIN_ERROR};
  }
  if (conjugationMethodValue == null || isTypedValueEmpty(conjugationMethodValue)) {
    return {status: 'missing', message: CONJUGATION_SITES_REQUIRE_METHOD_ERROR};
  }
  const {antibodyId, conjugationMethod} = getConjugationSiteInputs(proteinValue, conjugationMethodValue);
  return antibodyId != null && conjugationMethod != null
    ? {status: 'valid', antibodyId, conjugationMethod}
    : {status: 'invalid'};
}

/**
 * Looks up the candidate sites through the resolution cache. Returns null candidates while the lookup is
 * pending, or with the resolution error if it failed.
 */
function lookupConjugationSiteCandidates(
  typedValue: TypedValue,
  antibodyId: string,
  conjugationMethod: AdcConjugationMethod,
  resolveFromResolutionQuery: ResolveQueryFn
): {candidates: ConjugationSiteCandidatesResult | null; error: CustomEvalResult['error']} {
  const {resolvedValue, error} = resolveFromResolutionQuery(
    typedValue,
    {
      type: ResolutionQueryType.CUSTOM,
      customResolutionQueryType: CustomResolutionQueryType.REGISTRATION_TABLE_ADC_CONJUGATION_SITE_CANDIDATES,
      antibodyId,
      conjugationMethod,
    },
    makeTypedValueFromCustomResolvedValue
  );
  if (error != null || resolvedValue?.type !== BaseTypedValueType.JSON) {
    return {candidates: null, error: error ?? null};
  }
  return {candidates: resolvedValue.jsonValue as ConjugationSiteCandidatesResult, error: null};
}

/** The error message for selected sites that the candidates do not allow, or null if all are allowed. */
function getCandidatesErrorMessage(
  sites: Array<ConjugationSite>,
  candidates: ConjugationSiteCandidatesResult
): string | null {
  if ('error' in candidates) {
    return candidates.error;
  }
  if (!candidates.supported) {
    return CONJUGATION_SITES_UNSUPPORTED_METHOD_ERROR;
  }
  const candidateKeys = new Set(candidates.candidates.map((site) => conjugationSiteKey(site)));
  const invalidSiteCount = sites.filter((site) => !candidateKeys.has(conjugationSiteKey(site))).length;
  return invalidSiteCount > 0 ? invalidConjugationSitesError(invalidSiteCount) : null;
}

/**
 * Custom eval for the ADC Conjugation sites column, which checks the selected sites against the candidate
 * sites for the row's protein and conjugation method.
 *
 * cellDependencies layout (the column's dependencyColumnGroups in AdcRegTableColumnTransformer's ADC_COLUMN_SPECS):
 *   [0] = ADC_PROTEIN (antibody BIOENTITY)
 *   [1] = ADC_CONJUGATION_METHOD (AdcConjugationMethod STRING)
 *
 * An empty cell has no error; the column's required check covers it. A protein or method that is filled in
 * but invalid reports its own error, so the sites are not checked against it. The cell's value is always
 * preserved.
 */
export function adcConjugationSitesColumnCustomEval(
  typedValue: TypedValue,
  _columnDefinition: ColumnDefinition | null,
  cellDependencies: Array<TypedValue | null>,
  _customEvalOptions: unknown,
  resolveFromResolutionQuery: ResolveQueryFn
): CustomEvalResult {
  const result = (error: CustomEvalResult['error']): CustomEvalResult => ({
    val: typedValue,
    error,
    locked: false,
  });

  const sites = parseConjugationSitesTypedValue(typedValue);
  if (sites == null) {
    return result(new ExcessUtil.ValueError(CONJUGATION_SITES_MALFORMED_ERROR));
  }
  if (sites.length === 0) {
    return result(null);
  }

  const rowInputs = getConjugationSiteRowInputs(cellDependencies);
  if (rowInputs.status === 'missing') {
    return result(new ExcessUtil.ValueError(rowInputs.message));
  }
  if (rowInputs.status === 'invalid') {
    return result(null);
  }

  const {candidates, error} = lookupConjugationSiteCandidates(
    typedValue,
    rowInputs.antibodyId,
    rowInputs.conjugationMethod,
    resolveFromResolutionQuery
  );
  if (candidates == null) {
    return result(error);
  }
  const candidatesErrorMessage = getCandidatesErrorMessage(sites, candidates);
  return result(candidatesErrorMessage != null ? new ExcessUtil.ValueError(candidatesErrorMessage) : null);
}
